import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { EventService } from '../../services/event.service';
import { SunmiPrinterService } from '../../../../core/services/sunmi-printer.service';
import { BrowserDetector } from '../../../../core/utils/browser-detector.util';
import { ProgramService } from '../../../../core/services/program.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Batch Ticket Printing Component
 * Displays batches in a table with individual print controls
 */
@Component({
  selector: 'app-ticket-counter',
  standalone: true,
  imports: [CommonModule, ButtonModule, ToastModule],
  providers: [EventService, MessageService, SunmiPrinterService],
  templateUrl: './ticket-counter.component.html',
  styleUrls: ['./ticket-counter.component.scss']
})
export class TicketCounterComponent implements OnInit, OnDestroy {
  batches: BatchInfo[] = [];
  batchPrintCounts: Map<number, number> = new Map();
  printingBatches: Set<number> = new Set();
  currentProgramId: number = 0;

  // Print method tracking
  private printMethod: 'sunmi' | 'browser' | 'api' = 'browser';
  private isSunmiDevice = false;
  private destroy$ = new Subject<void>();

  constructor(
    private eventService: EventService,
    private messageService: MessageService,
    private sunmiPrinterService: SunmiPrinterService,
    private programService: ProgramService
  ) {}

  ngOnInit(): void {
    this.detectPrintingCapabilities();

    // Subscribe to program changes
    this.programService.programId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(programId => {
        this.currentProgramId = programId;
        this.loadMyBatches();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Detect browser and printing capabilities
   */
  private detectPrintingCapabilities(): void {
    // Log browser and device info
    BrowserDetector.logInfo();

    // Get recommended print method
    this.printMethod = BrowserDetector.getRecommendedPrintMethod();
    this.isSunmiDevice = BrowserDetector.detect().isSunmiDevice;


    // Show info message about print method
    if (this.printMethod === 'sunmi') {
      this.messageService.add({
        severity: 'success',
        summary: 'Sunmi Printer Detected',
        detail: 'Using native Sunmi printer for optimal performance',
        life: 3000
      });
    } else if (this.printMethod === 'api') {
      this.messageService.add({
        severity: 'info',
        summary: 'Print Service Mode',
        detail: 'Using browser print dialog. For better experience, use Sunmi WebView integration.',
        life: 5000
      });
    }
  }

  /**
   * Load current user's assigned batches
   */
  private loadMyBatches(): void {
    this.eventService.getMyBatches(this.currentProgramId, undefined, 1, 100).subscribe({
      next: (response) => {

        // Safely access the nested data structure
        if (response?.data?.items) {
          this.batches = response.data.items.filter((batch: any) =>
            batch.status === 'Assigned' || batch.status === 'InProgress'
          );


          // Initialize print counts for each batch
          this.batches.forEach(batch => {
            this.batchPrintCounts.set(batch.batchId, 0);
          });

          if (this.batches.length === 0) {
            this.messageService.add({
              severity: 'info',
              summary: 'No Batches',
              detail: 'No ticket batches assigned to you at the moment',
              life: 3000
            });
          }
        } else {
          this.batches = [];
          this.showError('Invalid response format from server');
        }
      },
      error: (err) => {
        this.batches = [];
        this.handleError(err, 'Failed to load assigned batches');
      }
    });
  }

  /**
   * Get print count for a batch
   */
  getPrintCount(batchId: number): number {
    return this.batchPrintCounts.get(batchId) || 0;
  }

  /**
   * Update print count for a batch
   */
  updatePrintCount(batchId: number, value: number): void {
    const batch = this.batches.find(b => b.batchId === batchId);
    if (!batch) return;

    const remainingTickets = batch.assignedTicketCount - batch.printedTicketCount;

    // Ensure value is within valid range
    if (value < 0) {
      value = 0;
    } else if (value > remainingTickets) {
      value = remainingTickets;
    }

    this.batchPrintCounts.set(batchId, value);
  }

  /**
   * Increment print count for a batch
   */
  incrementPrintCount(batchId: number): void {
    const currentCount = this.getPrintCount(batchId);
    const batch = this.batches.find(b => b.batchId === batchId);
    if (!batch) return;

    const remainingTickets = batch.assignedTicketCount - batch.printedTicketCount;
    if (currentCount < remainingTickets) {
      this.updatePrintCount(batchId, currentCount + 1);
    }
  }

  /**
   * Decrement print count for a batch
   */
  decrementPrintCount(batchId: number): void {
    const currentCount = this.getPrintCount(batchId);
    if (currentCount > 0) {
      this.updatePrintCount(batchId, currentCount - 1);
    }
  }

  /**
   * Check if print button should be disabled
   */
  isPrintDisabled(batch: BatchInfo): boolean {
    const printCount = this.getPrintCount(batch.batchId);
    const remainingTickets = batch.assignedTicketCount - batch.printedTicketCount;
    return printCount <= 0 || printCount > remainingTickets || this.printingBatches.has(batch.batchId);
  }

  /**
   * Check if batch is currently being printed
   */
  isPrinting(batchId: number): boolean {
    return this.printingBatches.has(batchId);
  }

  /**
   * Get remaining tickets for a batch
   */
  getRemainingTickets(batch: BatchInfo): number {
    return batch.assignedTicketCount - batch.printedTicketCount;
  }

  /**
   * Get total assigned tickets across all batches
   */
  getTotalAssignedTickets(): number {
    return this.batches.reduce((sum, batch) => sum + batch.assignedTicketCount, 0);
  }

  /**
   * Get total printed tickets across all batches
   */
  getTotalPrintedTickets(): number {
    return this.batches.reduce((sum, batch) => sum + batch.printedTicketCount, 0);
  }

  /**
   * Get total remaining tickets across all batches
   */
  getTotalRemainingTickets(): number {
    return this.batches.reduce((sum, batch) => sum + this.getRemainingTickets(batch), 0);
  }

  /**
   * Manual print - Opens image files in new tabs WITHOUT auto-print
   * User manually prints each tab with Ctrl+P / Cmd+P
   * Does not call API, just opens the images directly
   */
  manualPrintBatch(batch: BatchInfo): void {
    const printCount = this.getPrintCount(batch.batchId);

    if (printCount <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Print Count',
        detail: 'Please select a quantity to print',
        life: 3000
      });
      return;
    }

    // Create dummy ticket images for manual print
    // In production, these would be actual ticket images
    const dummyTicketImages: string[] = [];
    for (let i = 0; i < printCount; i++) {
      // Create a simple SVG ticket placeholder
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="400" height="600" viewBox="0 0 400 600">
          <rect width="400" height="600" fill="#f0f0f0" stroke="#333" stroke-width="2"/>
          <text x="200" y="50" text-anchor="middle" font-size="24" font-weight="bold" fill="#333">
            ${batch.batchNumber}
          </text>
          <text x="200" y="100" text-anchor="middle" font-size="18" fill="#666">
            Ticket ${i + 1} of ${printCount}
          </text>
          <text x="200" y="300" text-anchor="middle" font-size="16" fill="#888">
            Manual Print Mode
          </text>
          <text x="200" y="350" text-anchor="middle" font-size="14" fill="#999">
            ${batch.assignedUserName}
          </text>
          <text x="200" y="380" text-anchor="middle" font-size="12" fill="#999">
            ${batch.assignedUserEmail}
          </text>
        </svg>
      `;
      const base64Svg = btoa(unescape(encodeURIComponent(svg)));
      dummyTicketImages.push(`data:image/svg+xml;base64,${base64Svg}`);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Manual Print Mode',
      detail: `Opening ${printCount} image(s). Use Ctrl+P (Cmd+P) to print each tab.`,
      life: 5000
    });

    // Open images WITHOUT auto-print - user controls when to print
    this.openImagesInNewTabs(dummyTicketImages);

    // Reset print count
    this.batchPrintCounts.set(batch.batchId, 0);
  }

  /**
   * Open images directly in new tabs WITHOUT auto-print
   * Each image opens in its own tab for MANUAL printing
   * User presses Ctrl+P / Cmd+P to print when ready
   */
  private openImagesInNewTabs(ticketImages: string[]): void {

    ticketImages.forEach((imageData, index) => {
      // Ensure proper data URI format
      const image = imageData.startsWith('data:image')
        ? imageData
        : `data:image/png;base64,${imageData}`;

      // Small delay between opening tabs to avoid browser blocking
      setTimeout(() => {
        const newTab = window.open();
        if (newTab) {
          // Write image WITHOUT onload="window.print()" - user controls printing
          newTab.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Ticket ${index + 1} - Manual Print</title>
                <style>
                  @page { margin: 0; size: auto; }
                  body {
                    margin: 0;
                    padding: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background: #f0f0f0;
                  }
                  img {
                    max-width: 100%;
                    height: auto;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                  }
                  .print-hint {
                    position: fixed;
                    top: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #3b82f6;
                    color: white;
                    padding: 12px 24px;
                    border-radius: 8px;
                    font-family: Arial, sans-serif;
                    font-size: 14px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                    z-index: 1000;
                  }
                  @media print {
                    .print-hint { display: none; }
                  }
                </style>
              </head>
              <body>
                <div class="print-hint">📄 Press Ctrl+P (Cmd+P on Mac) to print</div>
                <img src="${image}" alt="Ticket ${index + 1}" />
              </body>
            </html>
          `);
          newTab.document.close();

          // NO auto-print - user manually prints when ready
        } else {
          console.warn('Failed to open tab for ticket', index + 1);
        }
      }, index * 200); // 200ms delay between each tab
    });

    if (ticketImages.length > 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'Manual Print Mode',
        detail: `${ticketImages.length} tabs opened. Press Ctrl+P (Cmd+P) in each tab to print.`,
        life: 7000
      });
    }
  }

  /**
   * Print tickets for a specific batch
   */
  printBatchTickets(batch: BatchInfo): void {
    const printCount = this.getPrintCount(batch.batchId);
    const remainingTickets = this.getRemainingTickets(batch);

    if (printCount <= 0 || printCount > remainingTickets) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Print Count',
        detail: `Please enter a valid number between 1 and ${remainingTickets}`,
        life: 3000
      });
      return;
    }

    // Mark batch as printing
    this.printingBatches.add(batch.batchId);

    // Prepare print request matching CounterTicketBatchPrintRequest
    const request = {
      batchId: batch.batchId,
      ticketCount: printCount,
      printedBy: undefined // Will be set by backend from authenticated user
    };


    // Call counter print API
    this.eventService.printBatchTickets(request).subscribe({
      next: (response) => {
        this.printingBatches.delete(batch.batchId);


        if (response.success && response.data) {
          const data = response.data;

          // Show success message
          if (data.failed > 0) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Partial Success',
              detail: `Printed ${data.successfullyPrinted} tickets. ${data.failed} failed. ${data.message || ''}`,
              life: 5000
            });
          } else {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: data.message || `Successfully printed ${data.successfullyPrinted} tickets from batch ${batch.batchNumber}`,
              life: 3000
            });
          }

          // Open ticket images in new tabs
          if (data.ticketImages && data.ticketImages.length > 0) {
            this.openTicketImages(data.ticketImages);
          }

          // Reset print count for this batch
          this.batchPrintCounts.set(batch.batchId, 0);

          // Reload batches to get updated counts
          this.loadMyBatches();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to print tickets',
            life: 5000
          });
        }
      },
      error: (err) => {
        this.printingBatches.delete(batch.batchId);
        this.handleError(err, 'Failed to print tickets');
      }
    });
  }

  /**
   * Print ticket images directly to the printer
   * Uses intelligent print strategy based on device and browser capabilities
   * Opens actual image files, not HTML pages
   */
  private openTicketImages(ticketImages: string[], printAll: boolean = true): void {


    // Strategy 1: Use Sunmi native printer if available
    if (this.printMethod === 'sunmi' && this.sunmiPrinterService.isSunmiPrinterAvailable()) {
      this.printUsingSunmi(ticketImages);
      return;
    }

    // Strategy 2: Open images with AUTO-PRINT (default for Print Tickets button)
    this.printImagesWithAutoprint(ticketImages);
  }

  /**
   * Print images with auto-print dialogs
   * Opens images in new window with auto-print for better POS compatibility
   */
  private printImagesWithAutoprint(ticketImages: string[]): void {

    // Use new window approach - works better with POS thermal printers in Chrome
    this.printUsingNewWindow(ticketImages);
  }

  /**
   * Print all tickets using hidden iframe - NO new windows
   * Print dialog appears directly in current page
   */
  private printAllTicketsInHiddenFrame(ticketImages: string[]): void {

    // Create hidden iframe
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow?.document;
    const frameWindow = printFrame.contentWindow;

    if (frameDoc && frameWindow) {
      // Build HTML with all ticket images
      let imagesHtml = '';
      ticketImages.forEach((base64Image, index) => {
        const imageData = base64Image.startsWith('data:image')
          ? base64Image
          : `data:image/png;base64,${base64Image}`;

        const pageBreak = index < ticketImages.length - 1 ? 'page-break-after: always;' : '';
        imagesHtml += `<div class="ticket-page" style="${pageBreak}">
          <img src="${imageData}" alt="Ticket ${index + 1}" />
        </div>`;
      });

      frameDoc.open();
      frameDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Tickets</title>
            <style>
              @page {
                margin: 0;
                size: auto;
              }
              body {
                margin: 0;
                padding: 0;
              }
              .ticket-page {
                margin: 0;
                padding: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              img {
                max-width: 100%;
                height: auto;
                display: block;
              }
              @media print {
                .ticket-page {
                  margin: 0;
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            ${imagesHtml}
          </body>
        </html>
      `);
      frameDoc.close();

      // Wait for images to load
      const images = frameDoc.querySelectorAll('img');
      let loadedCount = 0;
      const totalImages = images.length;

      if (totalImages === 0) {
        console.warn('No images found to print');
        document.body.removeChild(printFrame);
        this.messageService.add({
          severity: 'warn',
          summary: 'No Images',
          detail: 'No ticket images to print',
          life: 3000
        });
        return;
      }

      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          // All images loaded, trigger print
          setTimeout(() => {
            try {
              frameWindow.focus();
              frameWindow.print();

              this.messageService.add({
                severity: 'success',
                summary: 'Print Dialog Opened',
                detail: `Print dialog opened for ${totalImages} ticket(s)`,
                life: 3000
              });

              // Clean up after print
              setTimeout(() => {
                if (document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame);
                }
              }, 1000);

            } catch (error) {
              console.error('Print error:', error);
              if (document.body.contains(printFrame)) {
                document.body.removeChild(printFrame);
              }
              this.messageService.add({
                severity: 'error',
                summary: 'Print Error',
                detail: 'Failed to open print dialog',
                life: 5000
              });
            }
          }, 100);
        }
      };

      images.forEach((img, index) => {
        const htmlImg = img as HTMLImageElement;
        if (htmlImg.complete) {
          checkAllLoaded();
        } else {
          htmlImg.onload = () => checkAllLoaded();
          htmlImg.onerror = () => {
            console.error(`Failed to load ticket image ${index + 1}`);
            checkAllLoaded();
          };
        }
      });
    } else {
      console.warn('Failed to access iframe document');
      document.body.removeChild(printFrame);
      this.messageService.add({
        severity: 'error',
        summary: 'Print Failed',
        detail: 'Unable to initialize print',
        life: 5000
      });
    }
  }

  /**
   * Print using Sunmi native printer API
   */
  private printUsingSunmi(ticketImages: string[]): void {
    this.sunmiPrinterService.printImages(ticketImages).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Print Successful',
            detail: `${ticketImages.length} ticket(s) sent to Sunmi printer`,
            life: 3000
          });
        }
      },
      error: (error) => {
        console.error('Sunmi print failed:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Sunmi Print Failed',
          detail: 'Falling back to browser print. Error: ' + error.message,
          life: 5000
        });
        // Fallback to browser print
        this.printAllTickets(ticketImages);
      }
    });
  }

  /**
   * Print all tickets in a single print dialog
   * Uses multiple strategies based on browser compatibility
   */
  private printAllTickets(ticketImages: string[]): void {

    const browserInfo = BrowserDetector.detect();

    // Strategy selection based on browser
    if (browserInfo.name === 'firefox' || browserInfo.isMobile) {
      // Firefox and mobile browsers: Use new window/tab approach
      this.printUsingNewWindow(ticketImages);
    } else {
      // Chrome, Edge, Safari: Use iframe approach
      this.printUsingIframe(ticketImages);
    }
  }

  /**
   * Print using new window (works better in Firefox and mobile)
   */
  private printUsingNewWindow(ticketImages: string[]): void {

    // Build HTML with all ticket images
    let imagesHtml = '';
    ticketImages.forEach((base64Image, index) => {
      const imageData = base64Image.startsWith('data:image')
        ? base64Image
        : `data:image/png;base64,${base64Image}`;

      const pageBreak = index < ticketImages.length - 1 ? 'page-break-after: always;' : '';
      imagesHtml += `<div class="ticket-page" style="${pageBreak}">
        <img src="${imageData}" alt="Ticket ${index + 1}" />
      </div>`;
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Tickets</title>
          <style>
            @page {
              margin: 0;
              padding: 0;
              size: auto;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html, body {
              margin: 0;
              padding: 0;
              height: auto;
            }
            body {
              background: #f0f0f0;
            }
            .print-controls {
              position: fixed;
              top: 10px;
              right: 10px;
              z-index: 9999;
              display: flex;
              gap: 10px;
            }
            .print-btn, .close-btn {
              padding: 12px 24px;
              font-size: 16px;
              font-weight: bold;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              transition: all 0.3s ease;
            }
            .print-btn {
              background: #3b82f6;
              color: white;
            }
            .print-btn:hover {
              background: #2563eb;
              box-shadow: 0 4px 12px rgba(59,130,246,0.4);
            }
            .close-btn {
              background: #ef4444;
              color: white;
            }
            .close-btn:hover {
              background: #dc2626;
              box-shadow: 0 4px 12px rgba(239,68,68,0.4);
            }
            .ticket-page {
              margin: 0;
              padding: 20px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img {
              max-width: 100%;
              height: auto;
              display: block;
              margin: 0;
            }
            @media print {
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: white;
              }
              .print-controls {
                display: none !important;
              }
              .ticket-page {
                margin: 0 !important;
                padding: 0 0 10mm 0 !important;
                display: block;
                min-height: auto;
                page-break-inside: avoid;
              }
              img {
                margin: 0 !important;
                padding: 0 !important;
                width: 100%;
                height: auto;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-controls">
            <button class="print-btn" onclick="window.print()">🖨️ Print</button>
            <button class="close-btn" onclick="window.close()">✕ Close</button>
          </div>
          ${imagesHtml}
          <script>
            // Auto-print when images are loaded
            window.onload = function() {
              // Small delay to ensure rendering
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;

    // Open in new window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();

      this.messageService.add({
        severity: 'info',
        summary: 'Print Window Opened',
        detail: 'Print dialog will open automatically. Close the window when done.',
        life: 5000
      });
    } else {
      // Popup blocked - use blob URL approach
      console.warn('Popup blocked, using blob URL');
      this.printUsingBlobUrl(ticketImages);
    }
  }

  /**
   * Print using blob URL (fallback for popup blockers)
   */
  private printUsingBlobUrl(ticketImages: string[]): void {

    let imagesHtml = '';
    ticketImages.forEach((base64Image, index) => {
      const imageData = base64Image.startsWith('data:image')
        ? base64Image
        : `data:image/png;base64,${base64Image}`;

      const pageBreak = index < ticketImages.length - 1 ? 'page-break-after: always;' : '';
      imagesHtml += `<div class="ticket-page" style="${pageBreak}">
        <img src="${imageData}" alt="Ticket ${index + 1}" />
      </div>`;
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Tickets</title>
          <style>
            @page { margin: 0; size: auto; }
            body { margin: 0; padding: 0; }
            .ticket-page {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            img { max-width: 100%; height: auto; display: block; }
            @media print {
              .ticket-page { margin: 0; padding: 0; }
            }
          </style>
        </head>
        <body>${imagesHtml}</body>
      </html>
    `;

    const blob = new Blob([printContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    // Open blob URL in new tab
    const link = document.createElement('a');
    link.href = blobUrl;
    link.target = '_blank';
    link.download = 'tickets.html';
    link.click();

    // Cleanup
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

    this.messageService.add({
      severity: 'info',
      summary: 'Print Page Opened',
      detail: 'Use your browser\'s print function (Ctrl+P or Cmd+P) to print.',
      life: 7000
    });
  }

  /**
   * Print using iframe (original method for Chrome/Edge/Safari)
   */
  private printUsingIframe(ticketImages: string[]): void {
    // Create a hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow?.document;
    const frameWindow = printFrame.contentWindow;

    if (frameDoc && frameWindow) {
      // Build HTML with all ticket images
      let imagesHtml = '';
      ticketImages.forEach((base64Image, index) => {
        const imageData = base64Image.startsWith('data:image')
          ? base64Image
          : `data:image/png;base64,${base64Image}`;

        // Add page break after each image except the last one
        const pageBreak = index < ticketImages.length - 1 ? 'page-break-after: always;' : '';
        imagesHtml += `<div class="ticket-page" style="${pageBreak}">
          <img src="${imageData}" alt="Ticket ${index + 1}" />
        </div>`;
      });

      frameDoc.open();
      frameDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Tickets</title>
            <style>
              @page {
                margin: 0;
                size: auto;
              }
              body {
                margin: 0;
                padding: 0;
              }
              .ticket-page {
                margin: 0;
                padding: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              img {
                max-width: 100%;
                height: auto;
                display: block;
              }
              @media print {
                .ticket-page {
                  margin: 0;
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            ${imagesHtml}
          </body>
        </html>
      `);
      frameDoc.close();

      // Wait for all images to load
      const images = frameDoc.querySelectorAll('img');
      let loadedCount = 0;
      const totalImages = images.length;

      if (totalImages === 0) {
        console.warn('No images found to print');
        document.body.removeChild(printFrame);
        return;
      }

      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          // All images loaded, trigger print
          setTimeout(() => {
            try {
              // Track print status
              let printStarted = false;
              let printCompleted = false;

              // Listen for beforeprint event
              frameWindow.addEventListener('beforeprint', () => {
                printStarted = true;
              });

              // Listen for afterprint event
              frameWindow.addEventListener('afterprint', () => {
                printCompleted = true;

                // Show notification based on print status
                if (printStarted) {
                  this.messageService.add({
                    severity: 'success',
                    summary: 'Print Completed',
                    detail: `${totalImages} ticket(s) sent to printer successfully`,
                    life: 3000
                  });
                }

                // Clean up
                setTimeout(() => {
                  if (document.body.contains(printFrame)) {
                    document.body.removeChild(printFrame);
                  }
                }, 500);
              });

              // Set timeout to detect if print was cancelled
              const printTimeout = setTimeout(() => {
                if (!printCompleted) {
                  console.warn('Print dialog may have been cancelled or blocked');
                  this.messageService.add({
                    severity: 'warn',
                    summary: 'Print Status Unknown',
                    detail: 'Print dialog was closed. Please verify if tickets were printed.',
                    life: 5000
                  });

                  // Clean up
                  if (document.body.contains(printFrame)) {
                    document.body.removeChild(printFrame);
                  }
                }
              }, 60000); // 60 second timeout

              // Trigger print
              frameWindow.focus();

              // Try to print with error handling
              try {
                frameWindow.print();
              } catch (printError) {
                console.error('iframe print() failed:', printError);
                // Fallback to new window approach
                if (document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame);
                }
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Print Method Changed',
                  detail: 'Switching to alternative print method...',
                  life: 3000
                });
                // Fallback to new window
                this.printUsingNewWindow(ticketImages);
              }

            } catch (error) {
              console.error('Print setup error:', error);
              if (document.body.contains(printFrame)) {
                document.body.removeChild(printFrame);
              }
              this.messageService.add({
                severity: 'error',
                summary: 'Print Error',
                detail: 'Trying alternative print method...',
                life: 3000
              });
              // Fallback to new window
              this.printUsingNewWindow(ticketImages);
            }
          }, 100);
        }
      };

      images.forEach((img, index) => {
        const htmlImg = img as HTMLImageElement;
        if (htmlImg.complete) {
          checkAllLoaded();
        } else {
          htmlImg.onload = () => checkAllLoaded();
          htmlImg.onerror = () => {
            console.error(`Failed to load ticket image ${index + 1}`);
            this.messageService.add({
              severity: 'error',
              summary: 'Image Load Error',
              detail: `Failed to load ticket ${index + 1}. Print may be incomplete.`,
              life: 5000
            });
            checkAllLoaded(); // Continue even if one image fails
          };
        }
      });
    } else {
      console.warn('Failed to access iframe document');
      document.body.removeChild(printFrame);
      this.messageService.add({
        severity: 'warn',
        summary: 'Print Failed',
        detail: 'Switching to alternative method...',
        life: 3000
      });
      // Fallback to new window
      this.printUsingNewWindow(ticketImages);
    }
  }

  /**
   * Simple direct print (last resort fallback)
   * Opens images in current window for manual printing
   */
  private printDirectly(ticketImages: string[]): void {
    // Create a temporary container
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.backgroundColor = 'white';
    container.style.zIndex = '10000';
    container.style.overflow = 'auto';
    container.style.padding = '20px';

    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕ Close';
    closeBtn.style.position = 'fixed';
    closeBtn.style.top = '10px';
    closeBtn.style.right = '10px';
    closeBtn.style.padding = '10px 20px';
    closeBtn.style.backgroundColor = '#dc3545';
    closeBtn.style.color = 'white';
    closeBtn.style.border = 'none';
    closeBtn.style.borderRadius = '5px';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.zIndex = '10001';
    closeBtn.onclick = () => document.body.removeChild(container);

    // Add print button
    const printBtn = document.createElement('button');
    printBtn.textContent = '🖨️ Print';
    printBtn.style.position = 'fixed';
    printBtn.style.top = '10px';
    printBtn.style.right = '100px';
    printBtn.style.padding = '10px 20px';
    printBtn.style.backgroundColor = '#3b82f6';
    printBtn.style.color = 'white';
    printBtn.style.border = 'none';
    printBtn.style.borderRadius = '5px';
    printBtn.style.cursor = 'pointer';
    printBtn.style.zIndex = '10001';
    printBtn.onclick = () => window.print();

    container.appendChild(closeBtn);
    container.appendChild(printBtn);

    // Add images
    ticketImages.forEach((base64Image, index) => {
      const imageData = base64Image.startsWith('data:image')
        ? base64Image
        : `data:image/png;base64,${base64Image}`;

      const img = document.createElement('img');
      img.src = imageData;
      img.alt = `Ticket ${index + 1}`;
      img.style.maxWidth = '100%';
      img.style.marginBottom = '20px';
      img.style.display = 'block';
      img.style.pageBreakAfter = index < ticketImages.length - 1 ? 'always' : 'auto';

      container.appendChild(img);
    });

    document.body.appendChild(container);

    this.messageService.add({
      severity: 'info',
      summary: 'Print Ready',
      detail: 'Click the Print button or press Ctrl+P / Cmd+P to print',
      life: 5000
    });
  }

  /**
   * Print tickets one by one with a delay between each print
   */
  private printTicketSequentially(ticketImages: string[], index: number): void {
    if (index >= ticketImages.length) {
      this.messageService.add({
        severity: 'success',
        summary: 'All Tickets Processed',
        detail: `${ticketImages.length} ticket(s) completed`,
        life: 3000
      });
      return;
    }

    const base64Image = ticketImages[index];

    // Check if the base64 string has the data URI prefix
    const imageData = base64Image.startsWith('data:image')
      ? base64Image
      : `data:image/png;base64,${base64Image}`;

    // Create a hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow?.document;
    const frameWindow = printFrame.contentWindow;

    if (frameDoc && frameWindow) {
      frameDoc.open();
      frameDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Ticket ${index + 1}</title>
            <style>
              @page {
                margin: 0;
                size: auto;
              }
              body {
                margin: 0;
                padding: 0;
                display: flex;
                justify-content: center;
                align-items: center;
              }
              img {
                max-width: 100%;
                height: auto;
                display: block;
              }
            </style>
          </head>
          <body>
            <img src="${imageData}" alt="Ticket ${index + 1}" />
          </body>
        </html>
      `);
      frameDoc.close();

      // Wait for image to load before printing
      const img = frameDoc.querySelector('img');
      if (img) {
        img.onload = () => {
          // Small delay to ensure rendering is complete
          setTimeout(() => {
            try {
              let printStarted = false;

              // Listen for afterprint event
              frameWindow.addEventListener('afterprint', () => {
                console.log(`Ticket ${index + 1} print dialog closed`);

                // Clean up
                if (document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame);
                }

                // Print next ticket
                setTimeout(() => {
                  this.printTicketSequentially(ticketImages, index + 1);
                }, 500);
              });

              // Timeout in case afterprint doesn't fire
              setTimeout(() => {
                if (document.body.contains(printFrame)) {
                  console.warn(`Ticket ${index + 1} print timeout - continuing to next`);
                  document.body.removeChild(printFrame);
                  this.printTicketSequentially(ticketImages, index + 1);
                }
              }, 30000); // 30 second timeout per ticket

              // Trigger print
              frameWindow.focus();
              frameWindow.print();

            } catch (error) {
              console.error('Print error:', error);
              if (document.body.contains(printFrame)) {
                document.body.removeChild(printFrame);
              }
              this.messageService.add({
                severity: 'error',
                summary: 'Print Error',
                detail: `Failed to print ticket ${index + 1}. Continuing to next ticket.`,
                life: 3000
              });
              // Continue with next ticket even if this one failed
              this.printTicketSequentially(ticketImages, index + 1);
            }
          }, 100);
        };

        img.onerror = () => {
          console.error('Failed to load ticket image');
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Image Load Error',
            detail: `Failed to load ticket ${index + 1}. Skipping to next ticket.`,
            life: 3000
          });
          // Continue with next ticket
          this.printTicketSequentially(ticketImages, index + 1);
        };
      }
    } else {
      console.warn('Failed to access iframe document');
      if (document.body.contains(printFrame)) {
        document.body.removeChild(printFrame);
      }
      this.messageService.add({
        severity: 'warn',
        summary: 'Print Failed',
        detail: `Unable to print ticket ${index + 1}. Skipping to next ticket.`,
        life: 3000
      });
      // Continue with next ticket
      this.printTicketSequentially(ticketImages, index + 1);
    }
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private handleError(err: any, message: string): void {
    console.error(err);
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.error?.title || err?.error?.detail || message
    });
  }
}

// Interfaces
export interface BatchInfo {
  batchId: number;
  publicId: string;
  batchNumber: string;
  reservationId: number;
  programId: number;
  assignedUserId: string;
  assignedUserName: string;
  assignedUserEmail: string;
  assignedTicketCount: number;
  printedTicketCount: number;
  status: string;
  assignedAt: string;
  seatReservation: {
    id: number;
    eventId: number;
    galleryId: number;
    blockId: number;
    levelId: number;
    numberOfSeats: number;
    hasSeats: boolean;
    seats: string;
    action: string;
    status: string;
    type: string;
    publicId: string;
    programId: number;
    reservationNumber: string;
  };
  ticketCategorySummary: {
    categoryName: string;
    categoryPrice: number;
    totalCount: number;
    printedCount: number;
    availableCount: number;
  };
}

